/**
 * @file ComplaintModal.js
 * @version 2.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-04-22
 * @modified 2026-09-22
 * @description Modal for viewing, adding, and editing customer complaints.
 *              Linked POs/SOs/Invoices and Credit Notes are stored as JSONB arrays
 *              (snapshot: {type, id, xero_id, number, date}) and picked via searchable
 *              multi-select populated from the selected customer's history. Every chip
 *              opens the underlying document's own modal, in all three modes.
 *
 *              Every column a user could reasonably need to change is on the form.
 *              That includes the ones the edit form used to omit while still POSTing
 *              them — rep, the free-text supplier fallback, the historical delivery
 *              address, and the legacy reference / credit-note text carried over from
 *              the spreadsheet import. Omitting a field but sending it is how those
 *              values were being silently cleared on every save.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import ComplaintImages, { uploadPendingComplaintImages } from './ComplaintImages';
import './ContactModal.css';
import './Complaints.css';
import SearchableDropdown from './SearchableDropdown';
import Dropdown from './Dropdown';
import Input from './Input';
import DateInput from './DateInput';
import Textarea from './Textarea';
import AlertDialog from './AlertDialog';
import LoadingSpinner from './LoadingSpinner';
import AuditHistoryModal from './AuditHistoryModal';
import EntityLink from './EntityLink';
import { useSlideOutButton } from './SlideOutButton';
import { formatDateShort } from '../utils/dateFormatters';

const STATUS_OPTIONS = [
  { value: 'Open', label: 'Open' },
  { value: 'Settled', label: 'Settled' },
  { value: 'Closed', label: 'Closed' }
];

const NOTIFIED_OPTIONS = [
  { value: '', label: '—' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

const statusClass = (status, isArchived) => {
  if (isArchived) return 'archived';
  if (status === 'Open') return 'active';
  if (status === 'Settled') return 'settled';
  return 'draft';
};

function ComplaintModal({ complaint, mode = 'view', onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [hauliers, setHauliers] = useState([]);
  const [reps, setReps] = useState([]);
  const [customerDocs, setCustomerDocs] = useState([]);
  const [customerCreditNotes, setCustomerCreditNotes] = useState([]);
  const [docPickerValue, setDocPickerValue] = useState('');
  const [cnPickerValue, setCnPickerValue] = useState('');
  const [closing, setClosing] = useState(false);
  /* Photographs picked before a NEW complaint has been saved. There is no id
     to attach them to until the save returns one, so they wait here and go up
     immediately afterwards. */
  const [pendingImages, setPendingImages] = useState([]);

  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [viewData, setViewData] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const [formData, setFormData] = useState({
    account_id: '',
    status: 'Open',
    complaint_date: new Date().toISOString().slice(0, 10),
    rep_name: '',
    delivery_date: '',
    supplier_id: '',
    haulier_id: '',
    supplier_name: '',
    address_snapshot: '',
    complaint_text: '',
    supplier_notified: '',
    supplier_reference: '',
    action_taken: '',
    notes: '',
    reference: '',
    credit_note_number: '',
    linked_documents: [],
    linked_credit_notes: []
  });

  useSlideOutButton([complaint, mode, saving, formData.linked_documents.length, formData.linked_credit_notes.length]);

  // Load per-mode reference data
  useEffect(() => {
    if (mode !== 'view') {
      loadCustomers();
      loadSuppliers();
      loadHauliers();
      loadReps();
    }

    document.body.classList.add('modal-open');
    const cw = document.querySelector('.content-wrapper');
    if (cw) cw.classList.add('modal-open');

    if (complaint && mode === 'edit') {
      setFormData({
        account_id: complaint.account_id ? complaint.account_id.toString() : '',
        status: complaint.status || 'Open',
        complaint_date: complaint.complaint_date ? complaint.complaint_date.split('T')[0] : '',
        rep_name: complaint.rep_name || '',
        delivery_date: complaint.delivery_date ? complaint.delivery_date.split('T')[0] : '',
        supplier_id: complaint.supplier_id ? complaint.supplier_id.toString() : '',
        haulier_id: complaint.haulier_id ? complaint.haulier_id.toString() : '',
        supplier_name: complaint.supplier_name || '',
        address_snapshot: complaint.address_snapshot || '',
        complaint_text: complaint.complaint_text || '',
        supplier_notified:
          complaint.supplier_notified === true ? 'true' :
          complaint.supplier_notified === false ? 'false' : '',
        supplier_reference: complaint.supplier_reference || '',
        action_taken: complaint.action_taken || '',
        notes: complaint.notes || '',
        reference: complaint.reference || '',
        credit_note_number: complaint.credit_note_number || '',
        linked_documents: Array.isArray(complaint.linked_documents) ? complaint.linked_documents : [],
        linked_credit_notes: Array.isArray(complaint.linked_credit_notes) ? complaint.linked_credit_notes : []
      });
    }

    if (complaint && mode === 'view') loadComplaintDetails(complaint.id);

    return () => {
      document.body.classList.remove('modal-open');
      const c = document.querySelector('.content-wrapper');
      if (c) c.classList.remove('modal-open');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complaint, mode]);

  // Whenever the selected customer changes (add/edit), refresh documents + credit notes
  // and auto-fill rep from customer.sales_rep.
  useEffect(() => {
    if (mode === 'view' || !formData.account_id) {
      setCustomerDocs([]);
      setCustomerCreditNotes([]);
      return;
    }
    loadCustomerDocs(formData.account_id);
    loadCustomerCreditNotes(formData.account_id);
    // Rep auto-fill (only fire once reps are loaded and a rep isn't already explicitly chosen)
    const chosen = customers.find(c => c.id.toString() === formData.account_id);
    if (chosen && chosen.sales_rep && !formData.rep_name) {
      const match = reps.find(r => r.name.toLowerCase().trim() === chosen.sales_rep.toLowerCase().trim());
      if (match) setFormData(prev => ({ ...prev, rep_name: match.name }));
      else setFormData(prev => ({ ...prev, rep_name: chosen.sales_rep }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.account_id, customers, reps]);

  const loadCustomers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=CUSTOMER&limit=10000`, { credentials: 'include' });
      const data = await res.json();
      setCustomers(data.contacts || []);
    } catch (e) { console.error('Error loading customers:', e); }
  };

  const loadSuppliers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/contacts/suppliers-list`, { credentials: 'include' });
      const data = await res.json();
      setSuppliers(Array.isArray(data) ? data : (data.suppliers || []));
    } catch (e) { console.error('Error loading suppliers:', e); }
  };

  const loadHauliers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/transport/hauliers`, { credentials: 'include' });
      const data = await res.json();
      setHauliers(data.hauliers || []);
    } catch (e) { console.error('Error loading hauliers:', e); }
  };

  const loadReps = async () => {
    try {
      const res = await fetch(`${API_URL}/api/orders/sales-reps`, { credentials: 'include' });
      const data = await res.json();
      setReps((data.reps || []).filter(r => r.status !== 'archived' && r.status !== 'ARCHIVED'));
    } catch (e) { console.error('Error loading reps:', e); }
  };

  const loadCustomerDocs = async (accountId) => {
    try {
      const res = await fetch(`${API_URL}/api/complaints/customer-documents?account_id=${accountId}`, { credentials: 'include' });
      const data = await res.json();
      setCustomerDocs(data.documents || []);
    } catch (e) { console.error('Error loading customer documents:', e); setCustomerDocs([]); }
  };

  const loadCustomerCreditNotes = async (accountId) => {
    try {
      const res = await fetch(`${API_URL}/api/complaints/customer-credit-notes?account_id=${accountId}`, { credentials: 'include' });
      const data = await res.json();
      setCustomerCreditNotes(data.creditNotes || []);
    } catch (e) { console.error('Error loading customer credit notes:', e); setCustomerCreditNotes([]); }
  };

  const loadComplaintDetails = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/complaints/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setViewData(data.complaint);
    } catch (e) { console.error('Error loading complaint:', e); }
    finally { setLoading(false); }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      const c = document.querySelector('.content-wrapper');
      if (c) c.classList.remove('modal-open');
      onClose();
    }, 300);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /* Choosing a supplier account retires the free-text fallback: the display side
     coalesces the account name over it, so leaving both set stores a value that
     is never shown and cannot be corrected. Clearing it in the form means the
     user watches it go rather than finding it gone later. */
  const handleSupplierAccount = (value) => {
    setFormData(prev => ({
      ...prev,
      supplier_id: value,
      supplier_name: value ? '' : prev.supplier_name
    }));
  };

  // --- Linked-document picker (PO/SO/Invoice multi-select) ----------------
  const handlePickDocument = useCallback((docKey) => {
    if (!docKey) return;
    const [type, idStr] = docKey.split(':');
    const doc = customerDocs.find(d => d.type === type && String(d.id) === idStr);
    if (!doc) return;
    // Skip duplicates
    const already = formData.linked_documents.some(d => d.type === doc.type && d.id === doc.id);
    setFormData(prev => {
      const next = already ? prev.linked_documents : [
        ...prev.linked_documents,
        {
          type: doc.type,
          id: doc.id,
          xero_id: doc.xero_id || null,
          number: doc.number,
          date: doc.date ? String(doc.date).slice(0, 10) : null
        }
      ];
      // Auto-fill delivery_date from the newly-added doc if form date empty
      let newDelivery = prev.delivery_date;
      if (!already && !prev.delivery_date) {
        const sourceDate = doc.delivery_date || doc.date;
        if (sourceDate) newDelivery = String(sourceDate).slice(0, 10);
      }
      return { ...prev, linked_documents: next, delivery_date: newDelivery };
    });
    setDocPickerValue('');
  }, [customerDocs, formData.linked_documents]);

  const removeDocument = (doc) => {
    setFormData(prev => ({
      ...prev,
      linked_documents: prev.linked_documents.filter(d => !(d.type === doc.type && d.id === doc.id))
    }));
  };

  // --- Credit-note picker --------------------------------------------------
  const handlePickCreditNote = useCallback((cnIdStr) => {
    if (!cnIdStr) return;
    const cn = customerCreditNotes.find(c => String(c.id) === cnIdStr);
    if (!cn) return;
    const already = formData.linked_credit_notes.some(c => c.id === cn.id);
    if (!already) {
      setFormData(prev => ({
        ...prev,
        linked_credit_notes: [
          ...prev.linked_credit_notes,
          { id: cn.id, number: cn.number, date: cn.date ? String(cn.date).slice(0, 10) : null }
        ]
      }));
    }
    setCnPickerValue('');
  }, [customerCreditNotes, formData.linked_credit_notes]);

  const removeCreditNote = (cn) => {
    setFormData(prev => ({
      ...prev,
      linked_credit_notes: prev.linked_credit_notes.filter(c => c.id !== cn.id)
    }));
  };

  // --- Submit --------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.account_id) {
      setAlertDialog({ isOpen: true, message: 'Please select a customer', type: 'error' });
      return;
    }
    if (!formData.complaint_date) {
      setAlertDialog({ isOpen: true, message: 'Please enter a complaint date', type: 'error' });
      return;
    }
    if (!formData.complaint_text || !formData.complaint_text.trim()) {
      setAlertDialog({ isOpen: true, message: 'Please describe the complaint', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        account_id: parseInt(formData.account_id),
        status: formData.status,
        complaint_date: formData.complaint_date,
        rep_name: formData.rep_name || null,
        delivery_date: formData.delivery_date || null,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null,
        haulier_id: formData.haulier_id ? parseInt(formData.haulier_id) : null,
        // Sent as typed. Picking a supplier account clears the free-text box in the
        // form (handleSupplierAccount), so the two can never disagree without the
        // user having seen it happen.
        supplier_name: formData.supplier_name || null,
        address_snapshot: formData.address_snapshot || null,
        complaint_text: formData.complaint_text.trim(),
        supplier_notified:
          formData.supplier_notified === 'true' ? true :
          formData.supplier_notified === 'false' ? false : null,
        supplier_reference: formData.supplier_reference || null,
        action_taken: formData.action_taken || null,
        notes: formData.notes || null,
        reference: formData.reference || null,
        credit_note_number: formData.credit_note_number || null,
        linked_documents: formData.linked_documents,
        linked_credit_notes: formData.linked_credit_notes
      };

      const url = complaint ? `${API_URL}/api/complaints/${complaint.id}` : `${API_URL}/api/complaints`;
      const res = await fetch(url, {
        method: complaint ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (data.success) {
        /*
         * Now there is an id, send anything that was waiting for one.
         *
         * A failure here is reported but does NOT block the close: the
         * complaint itself is saved, and making that read as a failure because
         * a photograph did not attach would be worse than saying so plainly.
         */
        const newId = data.complaint?.id || complaint?.id;
        if (pendingImages.length > 0 && newId) {
          const result = await uploadPendingComplaintImages(newId, pendingImages);
          if (!result.success) {
            setAlertDialog({
              isOpen: true,
              type: 'warning',
              message: `The complaint was saved, but the photographs did not attach: ${result.error || 'upload failed'}. Open it and try again.`
            });
          }
          setPendingImages([]);
        }
        setClosing(true);
        document.body.classList.remove('modal-open');
        const c = document.querySelector('.content-wrapper');
        if (c) c.classList.remove('modal-open');
        setTimeout(() => onSave(), 300);
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to save complaint', type: 'error' });
      }
    } catch (error) {
      console.error('Save error:', error);
      setAlertDialog({ isOpen: true, message: 'Error saving complaint', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // --- Linked-document chip ------------------------------------------------
  /**
   * One chip, in every mode. The type badge + number open the document's own
   * modal; the date and the (edit-mode) remove button sit outside that control
   * so we never nest a button inside a button.
   *
   * A PO opens on its *Xero* id — PurchaseOrderModal queries on that, and the
   * local integer returns "not found" silently. Older links were saved without
   * it, so the server resolves it on read; if it still isn't there the chip
   * renders as plain text rather than a control that does nothing.
   */
  const DocChip = ({ doc, kind = 'document', onRemove }) => {
    const type = kind === 'credit' ? 'Credit' : (doc.type || 'Doc');
    const entityType =
      kind === 'credit' ? 'credit_note' :
      doc.type === 'PO' ? 'purchase_order' :
      doc.type === 'SO' ? 'customer_order' :
      doc.type === 'Invoice' ? 'invoice' : null;
    const entityId = doc.type === 'PO' ? doc.xero_id : doc.id;

    return (
      <span className={`doc-chip doc-chip-${(kind === 'credit' ? 'credit' : (doc.type || '')).toLowerCase()}`}>
        <EntityLink
          type={entityType}
          id={entityType ? entityId : null}
          className="doc-chip-open"
          variant="badge"
          title={`View ${doc.number}`}
        >
          <span className="doc-chip-type">{type}</span>
          <span className="doc-chip-number">{doc.number}</span>
        </EntityLink>
        {doc.date && <span className="doc-chip-date">{formatDateShort(doc.date)}</span>}
        {onRemove && (
          <button type="button" className="doc-chip-remove" onClick={() => onRemove(doc)} title="Remove">
            <span className="material-symbols-rounded">close</span>
          </button>
        )}
      </span>
    );
  };

  // --- View-mode rendering -------------------------------------------------
  // Renders a detail item only if value is present (no '-' placeholders).
  const DetailItem = ({ label, value }) => {
    if (value == null || value === '' || value === false) return null;
    return (
      <div className="detail-item">
        <span className="detail-label">{label}</span>
        <span className="detail-value">{value}</span>
      </div>
    );
  };

  const renderViewMode = () => {
    const c = viewData || complaint;
    if (!c) return null;
    if (loading) return <LoadingSpinner />;

    const docs = Array.isArray(c.linked_documents) ? c.linked_documents : [];
    const cns = Array.isArray(c.linked_credit_notes) ? c.linked_credit_notes : [];
    const supplierLabel = c.supplier_display || c.supplier_account_name || c.supplier_name || '';
    const notifiedLabel = c.supplier_notified === true ? 'Yes' : c.supplier_notified === false ? 'No' : '';

    return (
      <>
        <div className="detail-section">
          <div className="detail-grid">
            <DetailItem label="Ref" value={c.complaint_number} />
            <DetailItem label="Complaint Date" value={formatDateShort(c.complaint_date)} />
            <DetailItem label="Customer" value={c.customer_name} />
            <DetailItem label="Account No" value={c.account_no} />
            <DetailItem label="Rep" value={c.rep_name} />
            <DetailItem label="Delivery Date" value={c.delivery_date ? formatDateShort(c.delivery_date) : ''} />
            <DetailItem label="Supplier" value={supplierLabel} />
            <DetailItem label="Haulier" value={c.haulier_name} />
            <DetailItem label="Supplier Notified" value={notifiedLabel} />
            <DetailItem label="Supplier Ref" value={c.supplier_reference} />
            <DetailItem label="Delivery Address" value={c.address_snapshot} />
            <DetailItem label="Legacy Ref" value={c.reference} />
            <DetailItem label="Legacy Credit Note" value={c.credit_note_number} />
          </div>
        </div>

        {docs.length > 0 && (
          <div className="detail-section">
            <h4>Linked Documents</h4>
            <div className="doc-chip-list">
              {docs.map((d, idx) => (
                <DocChip key={`${d.type}-${d.id ?? idx}`} doc={d} />
              ))}
            </div>
          </div>
        )}

        {cns.length > 0 && (
          <div className="detail-section">
            <h4>Credit Notes</h4>
            <div className="doc-chip-list">
              {cns.map((cn, idx) => (
                <DocChip key={cn.id ?? idx} doc={cn} kind="credit" />
              ))}
            </div>
          </div>
        )}

        <div className="detail-section">
          <h4>Complaint</h4>
          <div className="notes-section">{c.complaint_text || ''}</div>
        </div>

        {c.action_taken && (
          <div className="detail-section">
            <h4>Action Taken</h4>
            <div className="notes-section">{c.action_taken}</div>
          </div>
        )}

        {c.notes && (
          <div className="detail-section">
            <h4>Case Notes</h4>
            <div className="notes-section">{c.notes}</div>
          </div>
        )}

        {/* The photographs, which for most complaints are the substance of it.
            Read-only here — attach and remove them in the editor. */}
        <div className="detail-section">
          <ComplaintImages complaintId={c.id} readOnly />
        </div>
      </>
    );
  };

  // --- Add/Edit rendering --------------------------------------------------
  /* SearchableDropdown renders nothing for a value it has no option for, so a rep
     name imported from the spreadsheet — or one since archived — would show as an
     empty box and be silently dropped on save. Carry it as its own option. */
  const repOptions = [
    { value: '', label: 'Select rep...' },
    ...reps.map(r => ({ value: r.name, label: r.name, searchText: r.name })),
    ...(formData.rep_name && !reps.some(r => r.name === formData.rep_name)
      ? [{ value: formData.rep_name, label: formData.rep_name, searchText: formData.rep_name }]
      : [])
  ];

  const docOptions = [
    { value: '', label: 'Search POs / SOs / Invoices by number or product...' },
    ...customerDocs
      .filter(d => !formData.linked_documents.some(ld => ld.type === d.type && ld.id === d.id))
      .map(d => {
        /*
         * The DELIVERY date, not the date the document was raised. A complaint
         * is about what turned up on the day, so the date somebody is trying to
         * match against is the delivery — the created date sent them looking at
         * the wrong week.
         *
         * Invoices carry no delivery date and nothing links them to a customer
         * order (checked: no invoice reference matches an order number), so
         * their cell is left empty rather than printing a lone dash.
         */
        const delivered = d.delivery_date ? formatDateShort(d.delivery_date) : null;
        return {
          value: `${d.type}:${d.id}`,
          badge: d.type,
          // Its own spans so the row can be a grid — 55px badge, 120px number,
          // the rest for the date. `display: contents` on the label wrapper is
          // what lets these two sit in the grid directly (ComplaintModal.css).
          renderLabel: (
            <>
              <span className="doc-opt-number">{d.number}</span>
              <span className="doc-opt-delivered">
                {delivered ? `Delivered: ${delivered}` : ''}
              </span>
            </>
          ),
          label: `${d.number}${delivered ? ` Delivered: ${delivered}` : ''}`,
          searchText: `${d.number} ${d.type} ${delivered || ''} ${d.product_text || ''}`
        };
      })
  ];

  const cnOptions = [
    { value: '', label: 'Search credit notes by number or product...' },
    ...customerCreditNotes
      .filter(cn => !formData.linked_credit_notes.some(lc => lc.id === cn.id))
      .map(cn => ({
        value: String(cn.id),
        badge: 'CN',
        label: `${cn.number}  —  ${cn.date ? formatDateShort(cn.date) : ''}`,
        searchText: `${cn.number} ${cn.product_text || ''}`
      }))
  ];

  /* Gate the legacy inputs on what the RECORD arrived with, not on the live form
     value — keying off formData would unmount the input the moment the user
     deleted the last character, with no way to get it back. */
  const hasLegacyReference = !!(complaint?.reference || formData.reference);
  const hasLegacyCreditNote = !!(complaint?.credit_note_number || formData.credit_note_number);

  const renderFormMode = () => (
    <form onSubmit={handleSubmit}>
      <div className="modal-body">
        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
          <div className="form-group">
            <label>Customer</label>
            <SearchableDropdown
              value={formData.account_id}
              onChange={(value) => handleChange('account_id', value)}
              options={[
                { value: '', label: 'Select customer...' },
                ...customers.map(c => ({
                  value: c.id.toString(),
                  badge: c.account_no || ' ',
                  label: c.company_name,
                  searchText: `${c.account_no || ''} ${c.company_name}`
                }))
              ]}
              placeholder="Select customer..."
            />
          </div>

          <div
            className="form-group"
            title="Pre-filled from the selected customer's contact record. Override it here if this complaint was handled by someone else."
          >
            <label>Rep</label>
            <SearchableDropdown
              value={formData.rep_name}
              onChange={(value) => handleChange('rep_name', value)}
              options={repOptions}
              placeholder="Select rep..."
            />
          </div>

          <div className="form-group">
            <label>Complaint Date</label>
            <DateInput
              value={formData.complaint_date}
              onChange={(e) => handleChange('complaint_date', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Delivery Date</label>
            <DateInput
              value={formData.delivery_date}
              onChange={(e) => handleChange('delivery_date', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Supplier</label>
            <SearchableDropdown
              value={formData.supplier_id}
              onChange={handleSupplierAccount}
              options={[
                { value: '', label: 'Select supplier...' },
                ...suppliers.map(s => ({
                  value: s.id.toString(),
                  badge: s.account_no || ' ',
                  label: s.company_name,
                  searchText: `${s.account_no || ''} ${s.company_name}`
                }))
              ]}
              placeholder="Select supplier..."
            />
          </div>

          <div className="form-group">
            <label>Haulier</label>
            <SearchableDropdown
              value={formData.haulier_id}
              onChange={(value) => handleChange('haulier_id', value)}
              options={[
                { value: '', label: 'None' },
                ...hauliers.map(h => ({
                  value: h.id.toString(),
                  label: h.name,
                  searchText: h.name
                }))
              ]}
              placeholder="Select haulier..."
            />
          </div>

          <div className="form-group">
            <label>Supplier Notified</label>
            <Dropdown
              value={formData.supplier_notified}
              onChange={(value) => handleChange('supplier_notified', value)}
              options={NOTIFIED_OPTIONS}
            />
          </div>

          <div className="form-group">
            <label>Supplier Ref</label>
            <Input
              type="text"
              value={formData.supplier_reference}
              onChange={(e) => handleChange('supplier_reference', e.target.value)}
              placeholder="Supplier's reference"
            />
          </div>

          {/* Free-text supplier fallback, for a one-off supplier that has no account.
              Hidden once an account is chosen — the account name wins on display, so
              showing an editable box that changes nothing would be a lie. */}
          {!formData.supplier_id && (
            <div className="form-group">
              <label>Supplier (not on account)</label>
              <Input
                type="text"
                value={formData.supplier_name}
                onChange={(e) => handleChange('supplier_name', e.target.value)}
                placeholder="Type a supplier name"
              />
            </div>
          )}

          <div className="form-group">
            <label>Delivery Address</label>
            <Input
              type="text"
              value={formData.address_snapshot}
              onChange={(e) => handleChange('address_snapshot', e.target.value)}
              placeholder="Address the delivery went to"
            />
          </div>

          {/* Superseded by the linked-document pickers below, so only offered on
              records that already carry one — imported spreadsheet rows, where the
              document referenced often does not exist in the system at all. Not
              shown on a new complaint: nothing should be typed here again. */}
          {hasLegacyReference && (
            <div className="form-group" title="Free-text reference carried over from the complaints spreadsheet. Prefer the linked documents below.">
              <label>Legacy Ref</label>
              <Input
                type="text"
                value={formData.reference}
                onChange={(e) => handleChange('reference', e.target.value)}
              />
            </div>
          )}

          {hasLegacyCreditNote && (
            <div className="form-group" title="Free-text credit note carried over from the complaints spreadsheet. Prefer the linked credit notes below.">
              <label>Legacy Credit Note</label>
              <Input
                type="text"
                value={formData.credit_note_number}
                onChange={(e) => handleChange('credit_note_number', e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Linked POs / SOs / Invoices */}
        <div className="form-group">
          <label>POs / SOs / Invoices</label>
          <SearchableDropdown
            value={docPickerValue}
            onChange={(v) => handlePickDocument(v)}
            options={docOptions}
            placeholder={formData.account_id ? 'Add a document...' : 'Select customer first'}
            searchPlaceholder="Search by number or product..."
            disabled={!formData.account_id}
          />
          {formData.linked_documents.length > 0 && (
            <div className="doc-chip-list" style={{ marginTop: 'var(--spacing-sm)' }}>
              {formData.linked_documents.map((d, idx) => (
                <DocChip key={`${d.type}-${d.id ?? idx}`} doc={d} onRemove={removeDocument} />
              ))}
            </div>
          )}
        </div>

        {/* Credit Notes */}
        <div className="form-group">
          <label>Credit Notes</label>
          <SearchableDropdown
            value={cnPickerValue}
            onChange={(v) => handlePickCreditNote(v)}
            options={cnOptions}
            placeholder={formData.account_id ? 'Add a credit note...' : 'Select customer first'}
            searchPlaceholder="Search by number or product..."
            disabled={!formData.account_id}
          />
          {formData.linked_credit_notes.length > 0 && (
            <div className="doc-chip-list" style={{ marginTop: 'var(--spacing-sm)' }}>
              {formData.linked_credit_notes.map((cn, idx) => (
                <DocChip key={cn.id ?? idx} doc={cn} kind="credit" onRemove={removeCreditNote} />
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Complaint</label>
          <Textarea
            value={formData.complaint_text}
            onChange={(e) => handleChange('complaint_text', e.target.value)}
            placeholder="Describe the complaint..."
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>Action Taken</label>
          <Textarea
            value={formData.action_taken}
            onChange={(e) => handleChange('action_taken', e.target.value)}
            placeholder="e.g. Credit issued, replacement delivered..."
            rows={2}
          />
        </div>

        <div className="form-group">
          <label>Case Notes</label>
          <Textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Additional notes..."
            rows={3}
          />
        </div>

        {/* On an existing complaint these upload straight away; on a new one
            they wait for the id the save returns. */}
        <div className="form-group">
          <ComplaintImages
            complaintId={complaint?.id || null}
            pendingFiles={pendingImages}
            onPendingChange={setPendingImages}
          />
        </div>
      </div>

      <div className="modal-footer">
        <button type="submit" className="btn-slideout btn-slideout-primary" disabled={saving}>
          <div className="icon-wrapper">
            <span className={`material-symbols-rounded ${saving ? 'spinning' : ''}`}>
              {saving ? 'sync' : 'save'}
            </span>
          </div>
          <span className="btn-label">{saving ? 'Saving...' : (complaint ? 'Update' : 'Create')}</span>
        </button>
        <button data-read-safe type="button" className="btn-round btn-danger" onClick={handleClose}>
          <div className="icon-wrapper">
            <span className="material-symbols-rounded">close</span>
          </div>
          <span className="btn-label">Cancel</span>
        </button>
      </div>
    </form>
  );

  const titleText = (() => {
    if (mode === 'view') return `Complaint ${complaint?.complaint_number || ''}`;
    if (mode === 'edit') return 'Edit Complaint';
    return 'New Complaint';
  })();

  // Title-bar status: view mode shows the existing status as a badge; add/edit shows a
  // small inline dropdown so the user can set it without the grid clutter.
  const headerStatusNode = (() => {
    if (mode === 'view') {
      const c = viewData || complaint;
      if (!c) return null;
      const label = c.is_archived ? 'Archived' : c.status;
      return (
        <span className={`status-badge ${statusClass(c.status, c.is_archived)}`} style={{ marginLeft: 'var(--spacing-md)' }}>
          {label}
        </span>
      );
    }
    // add/edit: inline status dropdown
    return (
      <div style={{ marginLeft: 'var(--spacing-md)', minWidth: '140px' }}>
        <Dropdown
          value={formData.status}
          onChange={(value) => handleChange('status', value)}
          options={STATUS_OPTIONS}
        />
      </div>
    );
  })();

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`modal-content modal-lg complaint-modal ${closing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{ paddingBottom: 'var(--spacing-lg)' }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <h3 style={{ marginBottom: '0' }}>{titleText}</h3>
            {headerStatusNode}
          </div>
          <div className="header-actions">
            {(viewData?.id || complaint?.id) && (
              <button
                className="btn-icon"
                onClick={() => setShowHistory(true)}
                title="View change history"
              >
                <span className="material-symbols-rounded">history</span>
              </button>
            )}
            <button className="btn-icon close-btn" onClick={handleClose} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>

        {/* View mode has no footer: its only button was a second Close, and the
            header already carries one. */}
        {mode === 'view' ? (
          <div className="modal-body">{renderViewMode()}</div>
        ) : (
          renderFormMode()
        )}

        <AlertDialog
          isOpen={alertDialog.isOpen}
          message={alertDialog.message}
          type={alertDialog.type}
          onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
        />

        <AuditHistoryModal
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          entityType="complaint"
          entityId={viewData?.id || complaint?.id}
          entityIdentifier={viewData?.complaint_number || complaint?.complaint_number}
        />
      </div>
    </div>,
    document.body
  );
}

export default ComplaintModal;
